// src/pages/auth/AcceptInvitationPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom'; // Fixed: useSearchParams instead of useParams
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { userService } from '../../services/userService';

const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams(); // Fixed: useSearchParams instead of useParams
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [invitationData, setInvitationData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    password_confirmation: ''
  });

  const invitationToken = searchParams.get('invitation_token'); // Fixed: get from search params

  useEffect(() => {
    if (invitationToken) {
      loadInvitationDetails();
    } else {
      setError('Invalid invitation link - no token found');
      setLoading(false);
    }
  }, [invitationToken]);

  const loadInvitationDetails = async () => {
    try {
      console.log('Loading invitation details for token:', invitationToken); // Added logging
      const data = await userService.getInvitationDetails(invitationToken);
      console.log('Invitation details received:', data); // Added logging
      
      setInvitationData(data);
      
      // Pre-fill form with invitation data
      setFormData(prev => ({
        ...prev,
        first_name: data.first_name || '',
        last_name: data.last_name || ''
      }));
    } catch (error) {
      console.error('Error loading invitation details:', error); // Added logging
      setError('Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      const acceptData = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        invitation_token: invitationToken
      };

      console.log('Submitting invitation acceptance:', acceptData); // Added logging
      await userService.acceptInvitation(acceptData);
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Error accepting invitation:', error); // Added logging
      setError(error.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6 text-center">
          <div className="text-green-600 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Aimee!</h2>
          <p className="text-gray-600 mb-4">
            Your account has been successfully created. You will be redirected to the login page.
          </p>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Registration</h1>
          <p className="text-gray-600 mt-2">Welcome to Aimee Medical Monitoring System</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <Input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="Enter your first name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <Input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="Enter your last name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a password (min 8 characters)"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <Input
              type="password"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Complete Registration'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;